import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { WandSparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { ToolbarButton } from "@/components/ui/toolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// The wand icon is an alternate trigger for the template list. Picking a
// template is an action, not a selection: the combobox is remounted after each
// pick so it returns to its placeholder.
function TemplateMenu({ templates = [], onSelect }) {
  const [key, setKey] = useState(0);

  const items = useMemo(
    () => templates.map(t => ({ value: t.id, label: t.name })),
    [templates]
  );

  const handleChange = next => {
    if (next) {
      onSelect(next.value);
      setKey(k => k + 1);
    }
  };

  return (
    <Combobox
      key={key}
      items={items}
      onValueChange={handleChange}
      itemToStringLabel={item => item?.label ?? ""}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <ToolbarButton
              render={
                <ComboboxPrimitive.Trigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Insert template"
                      // Pressing the control must not collapse the editor
                      // selection, or an appended template would land nowhere.
                      onMouseDown={event => event.preventDefault()}
                    />
                  }
                />
              }
            />
          }
        >
          <WandSparkles />
        </TooltipTrigger>
        <TooltipContent>Insert template</TooltipContent>
      </Tooltip>
      <ComboboxContent className="tw:min-w-52">
        <ComboboxInput showTrigger={false} placeholder="Search templates" />
        <ComboboxEmpty>No templates found.</ComboboxEmpty>
        <ComboboxList>
          {item => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

TemplateMenu.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func.isRequired,
};

export default TemplateMenu;
